import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { ServiceAgreement } from "@/contracts";

import { attestServiceSignatureMutation } from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { TextAreaField, TextField } from "@/components/ui/field";
import { FormDialog } from "@/components/ui/form-dialog";
import { useApiClient } from "@/lib/api-context";

const formSchema = z.object({
  signedOn: z.string().min(1),
  notes: z.string().trim().max(2_000),
});

type FormValues = z.infer<typeof formSchema>;

export function AttestServiceSignatureDialog({
  agreement,
  onClose,
}: {
  agreement: ServiceAgreement;
  onClose: () => void;
}) {
  const { t } = useTranslation("services");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      signedOn: new Date().toISOString().slice(0, 10),
      notes: "",
    },
  });

  const mutation = useMutation({
    ...attestServiceSignatureMutation(client),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["service-agreements"] });
      onClose();
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate({
      id: agreement.id,
      body: {
        signedOn: values.signedOn,
        ...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
      },
    });
  });

  return (
    <FormDialog
      open
      title={t("attestTitle")}
      submitLabel={t("attestSubmit")}
      pending={isSubmitting || mutation.isPending}
      onSubmit={(event) => void onSubmit(event)}
      onCancel={onClose}
    >
      <div className="flex flex-col gap-4">
        {mutation.error ? <Alert>{mutation.error.message}</Alert> : null}
        <TextField label={t("attestSignedOn")} type="date" required {...register("signedOn")} />
        <TextAreaField label={t("attestNotes")} {...register("notes")} />
      </div>
    </FormDialog>
  );
}
